import { Suspense, useState, useEffect } from 'react'
import { Outlet, useNavigate, Link } from 'react-router-dom'
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'react-bootstrap'
import Menu from '@views/menu'
import Footer from '@components/Footer'
import logoL from '@assets/images/logo_L.png'
import logoM from '@assets/images/logo_m.png'
import { auth } from '@/config/firebase'
import { signOut } from 'firebase/auth'
import { useAuth } from '@/context/AuthContext'
import { useAdminAccount } from '@/context/AdminAccountContext'
import { DotLoader } from 'react-spinners'

function MainLayout() {
  const [isMenuOpen, setIsMenuOpen] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const { user, loading: authLoading } = useAuth()
  const { adminAccount, isLoading: adminAccountLoading, resetAdminAccount } = useAdminAccount()
  const navigate = useNavigate()

  // 認証とAdminAccountのチェック
  useEffect(() => {
    // 認証とAdminAccountの読み込みが完了したらチェック
    if (authLoading || adminAccountLoading) {
      return
    }

    // 未ログインの場合はログイン画面へ
    if (!user) {
      navigate('/login', { replace: true })
      return
    }
    if (adminAccount && !adminAccount.lastLoginAt) {
      navigate('/first-login', { replace: true })
      return
    }
  }, [user, adminAccount, authLoading, adminAccountLoading, navigate])

  // ロード中
  if (authLoading || adminAccountLoading) {
    return (
      <div className="loading-overlay">
        <DotLoader color="var(--irogami-primary)" size={40} speedMultiplier={1} />
      </div>
    )
  }

  // リダイレクト待ち（useEffect が発火するまで何も描画しない）
  if (!user || !adminAccount || !adminAccount.lastLoginAt) {
    return null
  }

  // モバイル判定
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
      // モバイルの場合はデフォルトで閉じる
      if (window.innerWidth < 768) {
        setIsMenuOpen(false)
      }
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  // モバイルでメニューが開いている時、背景をクリックで閉じる
  const handleOverlayClick = () => {
    if (isMobile && isMenuOpen) {
      setIsMenuOpen(false)
    }
  }

  // ログアウト処理
  const handleLogout = async () => {
    try {
      // AdminAccountContextを初期化
      resetAdminAccount()
      // Firebase認証からログアウト
      await signOut(auth)
      navigate('/login', { replace: true })
    } catch (error) {
      console.error('ログアウトエラー:', error)
    }
  }

  return (
    <div className="app-container">
      {/* モバイル時のオーバーレイ */}
      {isMobile && isMenuOpen && (
        <div className="sidebar-overlay" onClick={handleOverlayClick}></div>
      )}
      <aside className={`sidebar ${isMobile ? (isMenuOpen ? 'open' : 'closed') : (isMenuOpen ? 'open' : 'collapsed')}`}>
        <div className="sidebar-header">
          <img
            src={isMenuOpen ? logoL : logoM}
            alt="IROGAMI"
            className="sidebar-logo"
            onClick={() => navigate('/dashboards')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <Menu isCollapsed={!isMobile && !isMenuOpen} />
      </aside>
      <div className="main-content-container">
        <header className="top-navbar">
          <div className="nav-left">
            <button 
              className="menu-toggle" 
              onClick={toggleMenu} 
              aria-label="メニューを開く/閉じる"
            >
              <i className="bi bi-list"></i>
            </button>
          </div>
          <div className="nav-right">
            <Dropdown align="end">
              <DropdownToggle as="div" className="user-info-wrapper dropdown-toggle" style={{ cursor: 'pointer' }}>
                <i className="bi bi-person-circle"></i>
                <span className="user-info">
                  {adminAccountLoading ? '読み込み中...' : adminAccount?.nameKanji ?? '管理者'}
                </span>
              </DropdownToggle>
              <DropdownMenu align="end">
                <DropdownItem as={Link} to="/account/my_account">
                  <i className="bi bi-person me-2"></i>
                  マイページ
                </DropdownItem>
                <DropdownItem onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right me-2"></i>
                  ログアウト
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </header>
        <main className="page-content">
          <Suspense
            fallback={
              <div className="page-content-route-loading">
                <DotLoader color="var(--irogami-primary)" size={40} speedMultiplier={1} />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
        <Footer />
      </div>
    </div>
  )
}

export default MainLayout

